import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronUp } from '@gravity-ui/icons';
import ViolationStatusBadge from '../ViolationsPage/components/ViolationStatusBadge';
import ViolationStateBlock from '../ViolationsPage/components/ViolationStateBlock';
import EvidencePanel from '../ViolationsPage/extensions/EvidencePanel';
import ResolutionContextPanel from '../ViolationsPage/extensions/ResolutionContextPanel';
import ReviewPanel from '../ViolationsPage/extensions/ReviewPanel';
import { getViolationEvent } from '../../services/violations';
import { getBusinessEvent } from '../../services/businessAnalytics';
import { getCamera } from '../../services/cameras';
import { getZone } from '../../services/zones';
import { isAdmin } from '../../services/api';
import { useRuleCatalog } from '../../hooks/useRuleCatalog';
import { formatEventCondition } from '../../utils/ruleFormat';
import { describeSingleTimezone } from '../../utils/cameraTimezone';
import { describeUserError } from '../../utils/apiError';
import { formatInTimezone } from '../../utils/formatInTimezone';
import { getOperatorLabel } from '../../constants/violationConstants';
import { ZONE_SCOPE_SPECIFIC, getZoneScopeLabel } from '../../utils/ruleZones';
import { emitAppEvent, APP_EVENTS } from '../../utils/appEvents';
import '../ViolationsPage/ViolationsPage.css';
import './ViolationEventDetailPage.css';

/*
 * P2-05 / Notification and deep-link context.
 * Этот маршрут — общий deep-link для Web Push и будущего Telegram: канал
 * уведомлений можно менять независимо от страницы нарушения.
 */

const ZONE_DELETED_LABEL = 'Зона удалена';
const ZONE_UNAVAILABLE_LABEL = 'Зона недоступна';

const ViolationEventDetailPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const admin = isAdmin();

  const [event, setEvent] = useState(null);
  const [camera, setCamera] = useState(null);
  const [pageState, setPageState] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [snapshotOpen, setSnapshotOpen] = useState(false);

  // ТЗ V5 №8: бизнес-событие из общего журнала читается своим endpoint.
  const isBusiness = new URLSearchParams(location.search).get('type') === 'business';

  const loadEvent = useCallback(() => {
    setPageState('loading');
    let loaded = null;

    (isBusiness ? getBusinessEvent(eventId) : getViolationEvent(eventId))
      .then((data) => {
        loaded = data;
        setEvent(data);
        setPageState('ready');
        // ТЗ 11: успешный GET карточки события двигает seen-cursor на бэкенде —
        // колокольчик должен перечитать summary немедленно, без локального
        // декремента (источник истины — сервер).
        emitAppEvent(APP_EVENTS.NOTIFICATIONS_REFRESH);
        if (!data?.camera_id) return null;
        return getCamera(data.camera_id).catch(() => null);
      })
      .then((cameraData) => {
        if (loaded) setCamera(cameraData);
      })
      .catch((error) => {
        const status = error?.response?.status;
        setEvent(null);
        if (status === 404) {
          setErrorMessage('Событие не найдено или недоступно');
          setPageState('notfound');
          return;
        }
        setErrorMessage(describeUserError(error, isBusiness ? 'Не удалось загрузить событие' : 'Не удалось загрузить нарушение'));
        setPageState('error');
      });
  }, [eventId, isBusiness]);

  useEffect(() => { loadEvent(); }, [loadEvent]);

  // Task 5: display_timezone теперь есть у самого события — он снимок на
  // момент нарушения и точнее текущей таймзоны камеры (которую могли сменить).
  const timezone = event?.display_timezone || camera?.display_timezone || undefined;
  const formatMoment = (value) => (value ? formatInTimezone(value, timezone) : '—');
  const { catalog } = useRuleCatalog(event?.camera_id);

  // Имя зоны берём из снимка правила события; если в снимке только id —
  // спрашиваем зону камеры. 404 значит, что зону позже удалили.
  const zoneSnap = event?.rule_snapshot && typeof event.rule_snapshot === 'object' ? event.rule_snapshot : null;
  const zoneSingleId = zoneSnap?.zone_id ?? event?.zone_id ?? null;
  const zoneSnapIds = Array.isArray(zoneSnap?.zone_ids) && zoneSnap.zone_ids.length > 0
    ? zoneSnap.zone_ids
    : (zoneSingleId ? [zoneSingleId] : []);
  const zoneSnapName = zoneSnap?.zone_display_name || zoneSnap?.zone_name || event?.zone_name || '';
  const zoneIdsKey = zoneSnapIds.join('|');
  const zoneCameraId = event?.camera_id;
  const [resolvedZones, setResolvedZones] = useState({});
  useEffect(() => {
    if (!zoneCameraId || zoneSnapName || zoneIdsKey === '') return undefined;
    let cancelled = false;
    zoneIdsKey.split('|').forEach((zoneId) => {
      getZone(zoneCameraId, zoneId)
        .then((zone) => {
          if (cancelled) return;
          setResolvedZones((prev) => ({ ...prev, [zoneId]: zone?.display_name || zone?.name || ZONE_UNAVAILABLE_LABEL }));
        })
        .catch((error) => {
          if (cancelled) return;
          setResolvedZones((prev) => ({
            ...prev,
            [zoneId]: error?.response?.status === 404 ? ZONE_DELETED_LABEL : ZONE_UNAVAILABLE_LABEL,
          }));
        });
    });
    return () => { cancelled = true; };
  }, [zoneCameraId, zoneIdsKey, zoneSnapName]);
  const cameraLabel = camera?.name || (event?.camera_id ? `Камера #${event.camera_id}` : '—');

  // ТЗ 5.2: camera_snapshot — исторический контекст события. Он не должен
  // меняться из-за последующего переименования или удаления камеры, поэтому
  // при его наличии показываем именно снапшот, а не текущее имя камеры.
  const snapshotPath = event?.camera_snapshot
    ? [
        event.camera_snapshot.factory?.name,
        event.camera_snapshot.workshop?.name,
        event.camera_snapshot.section?.name,
        event.camera_snapshot.room?.name,
      ].filter(Boolean).join(' / ')
    : '';

  const displayCameraLabel = event?.camera_snapshot?.camera?.name || cameraLabel;

  // Task 5 / ТЗ 8-11: react-router v7 хранит позицию в history.state.idx —
  // direct-open по deep-link (после логина, из уведомления) стартует с idx=0,
  // и navigate(-1) в этом случае увёл бы из приложения. Список при переходе
  // назад сам восстановит фильтры из URL (Task 4).
  // №19: из колокольчика пришли с чужим набором фильтров — «Назад» ведёт в общий
  // список, а не в предыдущую запись истории (она могла быть любой страницей).
  // Из диаграммы Ганта список событий в истории не открывался — ведём в общий
  // список и передаём событие: список сам найдёт его страницу и строку.
  const goBack = () => {
    if (location.state?.origin === 'gantt') {
      navigate('/violations', {
        state: event?.started_at
          ? { focusEvent: { id: event.id ?? Number(eventId), startedAt: event.started_at } }
          : undefined,
      });
      return;
    }
    if (location.state?.origin === 'bell') {
      navigate('/violations');
      return;
    }
    const canGoBack = window.history.state?.idx > 0;
    if (canGoBack) navigate(-1);
    else navigate('/violations');
  };

  const backButton = (
    <button type="button" className="ved-back" onClick={goBack}>
      <ChevronLeft width={16} height={16} aria-hidden />
      К событиям
    </button>
  );

  if (pageState === 'loading') {
    return (
      <div className="vio-container">
        {backButton}
        <ViolationStateBlock state="loading" />
      </div>
    );
  }

  if (pageState === 'notfound' || pageState === 'error') {
    return (
      <div className="vio-container">
        {backButton}
        <div className="vio-state vio-state--error">
          <h2 className="ved-unavailable-title">Событие недоступно</h2>
          <p className="vio-state-text">{errorMessage}</p>
          <div className="ved-unavailable-actions">
            <button type="button" className="vio-btn vio-btn--primary" onClick={() => navigate('/violations')}>
              К списку нарушений
            </button>
            {pageState === 'error' && (
              <button type="button" className="vio-btn" onClick={loadEvent}>Повторить</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const snapshot = event.rule_snapshot && typeof event.rule_snapshot === 'object' ? event.rule_snapshot : null;

  /*
   * ТЗ задача 7: zone_id=null — это «общая область контроля», а не «зоны
   * нет» — строка «Зона» рисуется всегда, независимо от наличия значения.
   *
   * FE-FIX29: область берётся из исторического snapshot события (zone_ids /
   * zone_scope), а не из текущего правила: правило могли позже перенастроить,
   * и событие от этого свою область не меняет.
   */
  const snapshotZoneIds = Array.isArray(snapshot?.zone_ids) ? snapshot.zone_ids : [];
  // ТЗ V5 №6: на кадре выделяются только зоны конкретной области правила;
  // общая область ни к какой зоне не привязана.
  const eventZoneIds = snapshot?.zone_scope === 'general' ? [] : zoneSnapIds;
  const eventSnapshotZones = eventZoneIds.map((zoneId) => ({
    zone_id: zoneId,
    display_name: eventZoneIds.length === 1 && zoneSnapName ? zoneSnapName : '',
  }));
  const baseZoneLabel = getZoneScopeLabel({
    zoneIds: snapshotZoneIds,
    zoneId: snapshot?.zone_id ?? event.zone_id ?? null,
    zoneScope: snapshot?.zone_scope,
    fallbackName: zoneSnapName,
  });
  const zoneLabel = (!zoneSnapName && zoneSnapIds.length > 0 && snapshot?.zone_scope !== 'general')
    ? zoneSnapIds
      .map((zoneId) => {
        const resolved = resolvedZones[zoneId];
        if (!resolved) return '…';
        return resolved === ZONE_DELETED_LABEL || resolved === ZONE_UNAVAILABLE_LABEL ? resolved : `«${resolved}»`;
      })
      .join(', ')
    : baseZoneLabel;
  // Условие упоминает зону только когда область правила действительно была
  // конкретной: для общей области ruleFormat сам подставит нужную фразу.
  const zoneWasSpecific = snapshot?.zone_scope === ZONE_SCOPE_SPECIFIC
    || snapshotZoneIds.length > 0
    || Boolean(snapshot?.zone_id);
  const durationSeconds = snapshot?.duration_seconds;
  const durationLabel = Number.isFinite(durationSeconds) ? `${durationSeconds} сек` : '';

  // Только шаги с фактическим значением: у активного события ещё нет ended_at.
  const timeline = [
    { key: 'started', label: 'Началось', value: event.started_at },
    { key: 'confirmed', label: 'Подтверждено', value: event.confirmed_at },
    { key: 'ended', label: 'Завершилось', value: event.ended_at },
  ].filter((step) => step.value);

  const subtitle = isBusiness
    ? (event.status === 'active' ? 'Событие продолжается' : 'Событие завершено')
    : (event.status === 'active' ? 'Нарушение продолжается' : 'Нарушение завершено');

  return (
    <div className="vio-container">
      {backButton}

      <div className="ved-breadcrumbs">События / Событие #{event.id}</div>
      <div className="ved-title-row">
        {/* Спека 5.1: заголовок должен быть человекочитаемым, без UUID.
            Fallback — `Событие #{id}`, без rule_id. */}
        <h1 className="ved-title">{event.rule_name || `Событие #${event.id}`}</h1>
        <ViolationStatusBadge status={event.status} withLabel />
        {event.excluded_from_analytics && (
          <span className="vio-badge vio-badge--deleted">Исключено из аналитики</span>
        )}
      </div>
      <p className="ved-subtitle">{subtitle}</p>

      <div className="ved-layout">
        <div className="ved-main">
          <EvidencePanel
            eventId={event.id}
            caption={`${displayCameraLabel} · ${formatMoment(event.confirmed_at)}`}
            cameraId={event.camera_id ?? null}
            zoneIds={eventZoneIds}
            snapshotZones={eventSnapshotZones}
          />
        </div>

        <div className="ved-side">
          <div className="vio-card">
            <p className="vio-drawer-section-title">Где</p>
            <div className="ved-fields-grid">
              <div>
                <div className="vio-readonly-label">Камера</div>
                <div className="vio-readonly-value">
                  {displayCameraLabel}
                  {snapshotPath && <div className="ved-snapshot-path">{snapshotPath}</div>}
                </div>
              </div>
            </div>
          </div>

          <div className="vio-card">
            <p className="vio-drawer-section-title">Когда</p>
            {/* FE-FIX10: времена карточки — в поясе камеры; называем его явно. */}
            <p className="vio-field-hint">{describeSingleTimezone(timezone)}</p>
            <ol className="ved-timeline">
              {timeline.map((step) => (
                <li key={step.key} className="ved-timeline-step ved-timeline-step--done">
                  <span className="ved-timeline-dot" />
                  <span className="ved-timeline-label">{step.label}</span>
                  {/* Отформатированное время — в тексте, исходный ISO — в title */}
                  <span className="ved-timeline-value" title={step.value}>{formatMoment(step.value)}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="vio-card">
            <p className="vio-drawer-section-title">Правило</p>
            <div className="ved-fields-grid">
              <div>
                <div className="vio-readonly-label">Правило</div>
                {/* Дублирует заголовок намеренно: карточка самодостаточна и читаема
                    отдельно от шапки (печать, скролл, узкий экран). */}
                <div className="vio-readonly-value">{event.rule_name || '—'}</div>
              </div>
              <div>
                <div className="vio-readonly-label">Условие</div>
                <div className="vio-readonly-value">{formatEventCondition(event, { zoneName: zoneWasSpecific ? zoneLabel : '', catalog })}</div>
              </div>
              <div>
                <div className="vio-readonly-label">Зона контроля</div>
                <div className="vio-readonly-value">{zoneLabel}</div>
              </div>
              {durationLabel && (
                <div>
                  <div className="vio-readonly-label">Длительность</div>
                  <div className="vio-readonly-value">{durationLabel}</div>
                </div>
              )}
            </div>
          </div>

          <ReviewPanel event={event} timezone={timezone} />
          <ResolutionContextPanel event={event} timezone={timezone} />
        </div>
      </div>

      {admin && snapshot && (
        <div className="vio-card">
          <button
            type="button"
            className="ved-collapsible"
            onClick={() => setSnapshotOpen((open) => !open)}
            aria-expanded={snapshotOpen}
          >
            <span>
              <span className="vio-drawer-section-title">Параметры правила на момент события</span>
              {event.rule_id && <span className="vio-field-hint"> {event.rule_id}</span>}
            </span>
            {snapshotOpen ? <ChevronUp width={18} height={18} /> : <ChevronDown width={18} height={18} />}
          </button>

          {snapshotOpen && (
            <div className="ved-snapshot">
              <div className="vio-readonly-grid">
                <div>
                  <div className="vio-readonly-label">Метрика</div>
                  <div className="vio-readonly-value">{snapshot.display_name || snapshot.metric || '—'}</div>
                </div>
                <div>
                  <div className="vio-readonly-label">Условие</div>
                  <div className="vio-readonly-value">
                    {getOperatorLabel(snapshot.operator)} {String(snapshot.threshold ?? '—')}
                  </div>
                </div>
                <div>
                  <div className="vio-readonly-label">Длительность</div>
                  <div className="vio-readonly-value">{snapshot.duration_seconds ?? '—'} сек</div>
                </div>
                <div>
                  <div className="vio-readonly-label">Допустимый разрыв</div>
                  <div className="vio-readonly-value">{snapshot.allowed_gap_seconds ?? '—'} сек</div>
                </div>
              </div>
              <pre className="ved-snapshot-json">{JSON.stringify(snapshot, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ViolationEventDetailPage;
